import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { formatCurrency, formatDateTime, STAGE_DISPLAY_NAMES } from '../lib/utils';
import { 
  ArrowLeft, 
  Building2, 
  Users,
  Briefcase,
  DollarSign,
  Clock,
  TrendingUp,
  Calendar,
  Mail,
  Phone,
  FileText,
  MessageSquare,
  ExternalLink,
  ChevronRight,
  Loader2
} from 'lucide-react';

const ACTIVITY_ICONS = {
  note: MessageSquare,
  call: Phone,
  meeting: Users,
  email: Mail,
  email_sent: Mail,
  stage_change: TrendingUp,
  created: FileText,
  deck_delivered: FileText,
  opportunity_created: TrendingUp,
  followup_set: Calendar,
};

const ACTIVITY_COLORS = {
  note: 'bg-blue-100 text-blue-700',
  call: 'bg-green-100 text-green-700',
  meeting: 'bg-purple-100 text-purple-700',
  email: 'bg-orange-100 text-orange-700',
  email_sent: 'bg-orange-100 text-orange-700',
  stage_change: 'bg-indigo-100 text-indigo-700',
  created: 'bg-gray-100 text-gray-700',
  deck_delivered: 'bg-amber-100 text-amber-700',
  opportunity_created: 'bg-emerald-100 text-emerald-700',
  followup_set: 'bg-cyan-100 text-cyan-700',
};

export default function AccountDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { get, loading } = useApi();
  
  const [accountData, setAccountData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await get(`/accounts/${id}`);
        setAccountData(data);
      } catch (error) {
        console.error('Failed to load account:', error);
        navigate('/pipeline');
      }
    };
    fetchData();
  }, [id]);

  if (loading || !accountData) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  const { account, stats, contacts, opportunities, timeline } = accountData;

  const renderActivityIcon = (type) => {
    const IconComponent = ACTIVITY_ICONS[type] || MessageSquare;
    return <IconComponent className="h-4 w-4" />;
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="account-detail-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate(-1)}
            className="rounded-sm"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="font-serif text-2xl sm:text-3xl flex items-center gap-2">
              <Building2 className="h-6 w-6" />
              {account.company_name}
            </h1>
            <div className="flex items-center gap-2 mt-1">
              {account.property_types?.map((pt) => (
                <Badge key={pt} variant="outline" className="rounded-sm text-xs">
                  {pt}
                </Badge>
              ))}
              {account.budget_cycles?.map((bc) => (
                <Badge key={bc} variant="secondary" className="rounded-sm text-xs">
                  {bc}
                </Badge>
              ))}
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card className="rounded-sm border">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <DollarSign className="h-4 w-4" />
                <span className="text-xs">Total Pipeline</span>
              </div>
              <p className="text-xl font-semibold">{formatCurrency(stats.total_pipeline_value)}</p>
            </CardContent>
          </Card>
          
          <Card className="rounded-sm border">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Briefcase className="h-4 w-4" />
                <span className="text-xs">Active Opps</span>
              </div>
              <p className="text-xl font-semibold">{stats.active_opportunities}</p>
            </CardContent>
          </Card>
          
          <Card className="rounded-sm border">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <TrendingUp className="h-4 w-4" />
                <span className="text-xs">Closed Won</span>
              </div>
              <p className="text-xl font-semibold text-emerald-600">{stats.closed_won}</p>
            </CardContent>
          </Card>
          
          <Card className="rounded-sm border">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Briefcase className="h-4 w-4" />
                <span className="text-xs">Total Opps</span>
              </div>
              <p className="text-xl font-semibold">{stats.total_opportunities}</p>
            </CardContent>
          </Card>
          
          <Card className="rounded-sm border">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Users className="h-4 w-4" />
                <span className="text-xs">Contacts</span>
              </div>
              <p className="text-xl font-semibold">{stats.total_contacts}</p>
            </CardContent>
          </Card>
          
          <Card className="rounded-sm border">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Clock className="h-4 w-4" />
                <span className="text-xs">Last Activity</span>
              </div>
              <p className="text-sm font-medium">
                {stats.last_activity_date ? formatDateTime(stats.last_activity_date) : 'N/A'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Tabbed Content */}
        <Tabs defaultValue="timeline" className="w-full">
          <TabsList className="grid w-full grid-cols-3 rounded-sm">
            <TabsTrigger value="timeline" className="rounded-sm" data-testid="account-timeline-tab">
              <MessageSquare className="h-4 w-4 mr-2" />
              Activity Timeline
            </TabsTrigger>
            <TabsTrigger value="contacts" className="rounded-sm" data-testid="account-contacts-tab">
              <Users className="h-4 w-4 mr-2" />
              Contacts ({contacts.length})
            </TabsTrigger>
            <TabsTrigger value="opportunities" className="rounded-sm" data-testid="account-opps-tab">
              <Briefcase className="h-4 w-4 mr-2" />
              Opportunities ({opportunities.length})
            </TabsTrigger>
          </TabsList>
          
          {/* Timeline Tab */}
          <TabsContent value="timeline" className="mt-4">
            <Card className="rounded-sm border">
              <CardHeader className="pb-4">
                <CardTitle className="text-base font-medium">
                  Activity Timeline ({timeline.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {timeline.length === 0 ? (
                  <div className="text-center py-8">
                    <MessageSquare className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                    <p className="text-muted-foreground">No activity yet</p>
                  </div>
                ) : (
                  <div className="space-y-0">
                    {timeline.map((activity, idx) => (
                      <div key={activity.id || idx} className="flex gap-4 relative">
                        {idx < timeline.length - 1 && (
                          <div className="absolute left-5 top-10 bottom-0 w-px bg-border" />
                        )}
                        <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${ACTIVITY_COLORS[activity.type] || 'bg-gray-100 text-gray-700'} z-10`}>
                          {renderActivityIcon(activity.type)}
                        </div>
                        <div className="flex-1 pb-6">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant="outline" className="text-xs rounded-sm capitalize">
                                  {activity.type.replace('_', ' ')}
                                </Badge>
                                {activity.entity_name && (
                                  <span className="text-xs text-muted-foreground">
                                    • {activity.entity_name}
                                  </span>
                                )}
                              </div>
                              <p className="text-sm">{activity.content}</p>
                            </div>
                            <div className="text-right text-xs text-muted-foreground whitespace-nowrap">
                              <p>{formatDateTime(activity.created_at)}</p>
                              <p className="mt-0.5">{activity.created_by_name}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Contacts Tab */}
          <TabsContent value="contacts" className="mt-4">
            <Card className="rounded-sm border">
              <CardHeader className="pb-4">
                <CardTitle className="text-base font-medium">
                  Contacts ({contacts.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {contacts.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                    <p className="text-muted-foreground">No contacts linked</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {contacts.map((contact) => (
                      <Link
                        key={contact.id}
                        to={`/contacts/${contact.id}`}
                        className="flex items-center justify-between p-3 bg-muted/30 rounded-sm hover:bg-muted/50 transition-colors group"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-sm font-medium text-primary">
                              {contact.first_name[0]}{contact.last_name[0]}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium">{contact.first_name} {contact.last_name}</p>
                            <p className="text-xs text-muted-foreground">{contact.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {contact.contact_type && (
                            <Badge variant="outline" className="rounded-sm text-xs">
                              {contact.contact_type}
                            </Badge>
                          )}
                          <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-1 transition-transform" />
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Opportunities Tab */}
          <TabsContent value="opportunities" className="mt-4">
            <Card className="rounded-sm border">
              <CardHeader className="pb-4">
                <CardTitle className="text-base font-medium">
                  Opportunities ({opportunities.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {opportunities.length === 0 ? (
                  <div className="text-center py-8">
                    <Briefcase className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                    <p className="text-muted-foreground">No opportunities</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {opportunities.map((opp) => (
                      <Link
                        key={opp.id}
                        to={`/opportunity/${opp.id}`}
                        className="flex items-center justify-between p-3 bg-muted/30 rounded-sm hover:bg-muted/50 transition-colors group"
                      >
                        <div>
                          <p className="font-medium">{opp.company_name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="rounded-sm text-xs">
                              {STAGE_DISPLAY_NAMES[opp.stage] || opp.stage}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {opp.probability}% probability
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-medium">{formatCurrency(opp.deal_value)}</span>
                          <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-1 transition-transform" />
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
